import { AuthPanel } from './auth-panel'
import { AuthAgreementCheckbox } from './auth-agreement-checkbox'
import { Button, InputField } from '@/shared/ui'
import { ArrowLeftIcon } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { cn } from '@/shared/lib'

export type AuthPanelProfileVariant = 'desktop' | 'mobile'

export type AuthPanelProfileProps = {
  variant?: AuthPanelProfileVariant
  title?: string
  nameLabel?: string
  namePlaceholder?: string
  nameValue?: string
  onNameChange?: (value: string) => void
  telegramLabel?: string
  telegramPlaceholder?: string
  telegramValue?: string
  onTelegramChange?: (value: string) => void
  jobTitleLabel?: string
  jobTitlePlaceholder?: string
  jobTitleValue?: string
  onJobTitleChange?: (value: string) => void
  companyLabel?: string
  companyPlaceholder?: string
  companyValue?: string
  onCompanyChange?: (value: string) => void
  showProfileFields?: boolean
  showAgreementCheckbox?: boolean
  agreementChecked?: boolean
  onAgreementChange?: (checked: boolean) => void
  agreementErrorText?: string
  legalLinksReturnTo?: string
  submitLabel?: string
  onSubmit?: () => void
  bottomLinkLabel?: string
  onBottomLinkClick?: () => void
  onBack?: () => void
  className?: string
}

const bottomLinkStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  fontSize: designTokens.typography.body.base.size,
  lineHeight: 'normal',
  fontWeight: designTokens.typography.body.base.weight,
  color: designTokens.colors.text.primary,
}

export function AuthPanelProfile({
  variant = 'desktop',
  title = 'Создаем ваш аккаунт',
  nameLabel = 'Имя',
  namePlaceholder = 'Ваше имя',
  nameValue,
  onNameChange,
  telegramLabel = 'Телеграм',
  telegramPlaceholder = '@username',
  telegramValue,
  onTelegramChange,
  jobTitleLabel = 'Должность',
  jobTitlePlaceholder = 'Ваша роль',
  jobTitleValue,
  onJobTitleChange,
  companyLabel = 'Компания',
  companyPlaceholder = 'Название компании',
  companyValue,
  onCompanyChange,
  showProfileFields = true,
  showAgreementCheckbox = false,
  agreementChecked,
  onAgreementChange,
  agreementErrorText,
  legalLinksReturnTo,
  submitLabel = 'Перейти к оформлению подписки',
  onSubmit,
  bottomLinkLabel = 'Уже есть аккаунт? Войти',
  onBottomLinkClick,
  onBack,
  className,
}: AuthPanelProfileProps) {
  const isMobile = variant === 'mobile'

  const desktopBack = (
    <button
      type="button"
      className="inline-flex items-center"
      onClick={onBack}
      style={{ gap: designTokens.spacing.authPanelBackGap }}
    >
      <ArrowLeftIcon
        className="h-5 w-5"
        aria-hidden="true"
        style={{ color: designTokens.colors.text.primary }}
      />
      <span
        style={{
          fontFamily: designTokens.typography.fontFamilies.primary,
          fontSize: designTokens.typography.body.base.size,
          lineHeight: 'normal',
          fontWeight: designTokens.typography.body.base.weight,
          color: designTokens.colors.text.primary,
        }}
      >
        Назад
      </span>
    </button>
  )

  const mobileBack = (
    <Button type="button" variant="tertiaryCancelIcon" onClick={onBack} aria-label="Back">
      <ArrowLeftIcon className="h-8 w-8" aria-hidden="true" />
    </Button>
  )

  const backLink = onBack ? (isMobile ? mobileBack : desktopBack) : undefined

  return (
    <AuthPanel
      variant={isMobile ? 'mobile' : 'desktop'}
      title={title}
      backLink={backLink}
      minHeight={isMobile ? designTokens.sizes.minHeight.authPanelProfileMobile : undefined}
      className={cn(className)}
      onSubmit={onSubmit}
      footer={
        <div
          className="flex w-full flex-col"
          style={{
            gap: designTokens.spacing.authPanelFooterGap,
            marginTop: isMobile ? '30px' : undefined,
          }}
        >
          <Button type="submit" variant={isMobile ? 'primaryMobile' : 'primary'}>
            {submitLabel}
          </Button>
          {onBottomLinkClick ? (
            <div className="flex w-full justify-center">
              <button type="button" style={bottomLinkStyle} onClick={onBottomLinkClick}>
                {bottomLinkLabel}
              </button>
            </div>
          ) : null}
        </div>
      }
    >
      <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.lg }}>
        {showProfileFields ? (
          <>
            <InputField
              label={nameLabel}
              placeholder={namePlaceholder}
              value={nameValue}
              onChange={(event) => onNameChange?.(event.target.value)}
            />
            <InputField
              label={telegramLabel}
              placeholder={telegramPlaceholder}
              value={telegramValue}
              onChange={(event) => onTelegramChange?.(event.target.value)}
            />
            <div
              className={cn('w-full', isMobile ? 'flex flex-col' : 'grid grid-cols-2')}
              style={{ gap: designTokens.spacing.md }}
            >
              <InputField
                label={jobTitleLabel}
                placeholder={jobTitlePlaceholder}
                value={jobTitleValue}
                onChange={(event) => onJobTitleChange?.(event.target.value)}
              />
              <InputField
                label={companyLabel}
                placeholder={companyPlaceholder}
                value={companyValue}
                onChange={(event) => onCompanyChange?.(event.target.value)}
              />
            </div>
          </>
        ) : null}
        {showAgreementCheckbox ? (
          <AuthAgreementCheckbox
            id="auth-profile-agreement-checkbox"
            checked={agreementChecked}
            onCheckedChange={onAgreementChange}
            errorText={agreementErrorText}
            legalLinksReturnTo={legalLinksReturnTo}
          />
        ) : null}
      </div>
    </AuthPanel>
  )
}
